import api from './api';
import type { ApiResponse } from '@/types/api.types';

/**
 * Build identity the backend reports on GET /health/live (no auth needed —
 * the login page asks too). In a monorepo build the frontend and backend
 * share one commit, so a mismatch here means the two images were deployed
 * from different builds; the version badge points that out.
 */
export interface BackendBuild {
  version: string;
  git_commit: string;
  build_time: string;
}

let cached: Promise<BackendBuild | null> | null = null;

const RELOAD_KEY = 'console.reloaded-for-backend';

/**
 * QA #45: after a deploy a browser can keep running the previous build's
 * bundle — the tab was already open, or index.html came out of the cache —
 * and call API paths the new backend no longer has. When the backend reports
 * a commit other than the one this bundle was built from, reload once per
 * backend commit so the browser fetches the matching bundle. The guard lives
 * in sessionStorage (per tab): if the reload still lands on the old bundle
 * (a proxy cache, or a rollout still in progress) nothing loops, and the
 * version badge keeps saying 後端不同版. Returns true when a reload was issued.
 */
export function reloadOnceForBackendBuild(backendCommit: string): boolean {
  if (!backendCommit) return false;
  try {
    if (sessionStorage.getItem(RELOAD_KEY) === backendCommit) return false;
    sessionStorage.setItem(RELOAD_KEY, backendCommit);
  } catch {
    return false; // storage unavailable: never risk a reload loop
  }
  window.location.reload();
  return true;
}

export const versionService = {
  /**
   * Fetch once per page load and share the result between the login page and
   * the sidebar. Any failure (backend down, probe path not routed in a local
   * setup) resolves to null: the badge then shows the frontend build only.
   */
  getBackendBuild(): Promise<BackendBuild | null> {
    if (!cached) {
      cached = api
        .get<ApiResponse<BackendBuild>>('/health/live', { skipGlobalError: true })
        .then((response) => response.data.data ?? null)
        .catch(() => null);
    }
    return cached;
  },
};
